//------------------------------------------------------------------------------
// BFA10102G 共架申込 基本情報 - 電柱選択
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { CellRange } from '@grapecity/wijmo.grid';
import { GridItems } from '@/utils/grid-items';
import MessageArea from '@/components/molecules/WarningMessageArea';
import CustomFlexGrid from '@/components/molecules/CustomFlexGrid';
import PoleItem from '@/utils/pole-item';

// 電柱一覧項目
const FIXED_SIZE = { quickAutoSize: false };
const GRID_ITEMS = new GridItems()
  .text('areaName',   80, '地区',     FIXED_SIZE)
  .text('lineName',  100, '冠称',     FIXED_SIZE)
  .text('poleText',  100, '電柱番号', FIXED_SIZE)
  .text('poleKind',   80, '施設名',   FIXED_SIZE)
;

// 電柱一覧グリッド属性
const GRID_PROPS = {
  headersVisibility: 'All',      // 列ヘッダー/列ヘッダー表示
  rowHeaderType: 'Row',          // 単一行選択
  filterOn: true,                // フィルタ有効
  allowSorting: 'SingleColumn',  // 単一列ソート
  allowDragging: 'None',         // ドラッグ不可
  AddDeleteOn: false,            // 追加削除不可
  isReadOnly: true,              // 読み取り専用
  style: {                       // 最大サイズ無効化
    height: '100%',
  },

  // 拡張プロパティ
  flexGridProps: {
    showSelectedHeaders: 'Row',  // 選択行にCSSクラス
  }
};

/**
 * 電柱選択 - 電柱一覧
 */
export default function PoleSelectList({
  poleList,
  selected,
  warnings,
  onSelect,
}) {

  // 電柱一覧参照 (CustomFlexGrid)
  const customRef = React.useRef();

  // 電柱一覧グリッド設定
  const gridView = GRID_ITEMS.createComponents();

  // 電柱一覧のデータ更新
  React.useEffect(() => {
    const custom = customRef.current;
    if (custom?.grid) {

      // 一覧設定
      const source = poleList.map((pole) => ({
        ...pole,
        poleText: PoleItem.text(pole),
      }));
      custom.setItemsSource(source);

      // 選択解除
      custom.clearSelectedState();
    }
  }, [poleList]);

  // 電柱一覧の選択状態更新
  React.useEffect(() => {
    const grid = customRef.current?.grid;
    if (!grid) return;

    // 選択電柱と同じ電柱を探す
    if (selected) {
      const selectedRow = grid.rows.find(
          (row) => selected.same(row.dataItem));
      if (selectedRow) {

        // 行選択状態を更新してスクロール
        const row = selectedRow.dataIndex;
        grid.select(new CellRange(row, -1), true);
        return;
      }
    }

    // 選択解除
    grid.select(-1, -1);
  }, [poleList, selected]);

  // 電柱一覧の選択変更処理
  const handleSelectionChange = (s, e) => {

    // 選択解除時はスキップ
    if (!e.range?.isValid) {
      return;
    }

    // 電柱選択変更
    const row = s.rows.at(e.row);
    if (row?.dataItem) {
      const pole = row.dataItem;
      if (!selected?.same(pole)) {

        // 新しい電柱を選択
        onSelect(new PoleItem({
            areaCode: pole.areaCode,
            areaName: pole.areaName,
            lineCode: pole.lineCode,
            lineName: pole.lineName,
            poleNo: pole.poleNo,
            poleKind: pole.poleKind,
            latitude: pole.latitude,
            longitude: pole.longitude,
        }));
      }
    }
  };

  //
  // 電柱一覧描画
  //
  return (
    <Stack
      sx={{
        px: 1, py: 1,
        my: 0, height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* メッセージ */}
      <Box>
        <MessageArea messages={warnings}/>
      </Box>

      {/* 電柱一覧 (検索結果) */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          maxHeight: '100%',

          // 選択電柱の行ヘッダーにアイコンを描画
          '& .wj-rowheaders .wj-header.wj-state-multi-selected::before': {
            content: '""',
            width: '20px',
            height: '20px',
            backgroundImage: 'url("/icons/check-24x24.png")',
            backgroundSize: 'contain',
          },

          // 選択行をMUIテーマの色にする
          '& .wj-cells .wj-cell.wj-state-multi-selected': {
            color: 'primay.textColor',
            bgcolor: 'primary.main',
          },
        }}
      >
        <CustomFlexGrid ref={customRef}
          initializedFunction={handleInitialize}
          selectionChangedFunction={handleSelectionChange}
          {...GRID_PROPS}
        >
          {gridView}
        </CustomFlexGrid>
      </Box>
    </Stack>
  );
}

// プロパティ
PoleSelectList.propTypes = {
  selected: PropTypes.object,               // 選択電柱
  poleList: PropTypes.array.isRequired,     // 電柱一覧
  warnings: PropTypes.array.isRequired,     // 警告メッセージ
  onSearch: PropTypes.func.isRequired,      // 電柱検索処理
  onSelect: PropTypes.func.isRequired,      // 電柱選択処理
};

// グリッド初期化処理
function handleInitialize(grid) {

  // 左上セルにタイトルを設定
  if (grid?.topLeftCells) {
    grid.topLeftCells.setCellData(0, 0, '選択');
  }
}
